'use client'

import styles from './Icon.module.css'
import classNames from 'classnames'
import type { CSSProperties } from 'react'
import { useEffect, useState } from 'react'
import type { IconNames, IconColours } from './Icon.types'

/**
 * All SVG icons coming from the design team
 * should follow this structure for consistency:
 * https://huel.atlassian.net/wiki/spaces/ENG/pages/606633985/Icon+System+SVG+Export
 */
const getFallbackIcon = () =>
  `<svg viewBox="0 0 40 40"><g><path d="M16.8,12.2v6h6.4v-6h3.4V27.8H23.2V21.1H16.8v6.7H13.4V12.2Z"/><g><path d="M22.9,39.8A20.1,20.1,0,0,1,.1,17.5,20.3,20.3,0,0,1,19,0a18.5,18.5,0,0,1,6.2.7,2,2,0,0,1,1.3,2.7h0a2,2,0,0,1-2.4,1.1A17.5,17.5,0,0,0,19.5,4,16,16,0,1,0,36,21.3a17.5,17.5,0,0,0-.3-4.6A1.9,1.9,0,0,1,37,14.4h.1a1.9,1.9,0,0,1,2.4,1.3,21.1,21.1,0,0,1,.3,7.5A20.1,20.1,0,0,1,22.9,39.8Z"/><path d="M32.1,9.5a12.6,12.6,0,0,0-2.4-2.2,2,2,0,0,1-.5-2.7h0a2,2,0,0,1,2.9-.5,15.8,15.8,0,0,1,3,2.8,2,2,0,0,1-.4,3h0A2,2,0,0,1,32.1,9.5Z"/></g></g></svg>`

const getSvg = async (name: string): Promise<string> => {
  const svgAssetUrl = `https://cdn.huel.io/ui-kit-icons/${name}.svg`
  let svg = sessionStorage.getItem(name) || ''

  if (!svg) {
    try {
      const call = await fetch(svgAssetUrl)

      if (call.status !== 200) {
        svg = getFallbackIcon()
      } else {
        svg = await call.text()
      }

      sessionStorage.setItem(name, svg)
    } catch (error) {
      console.warn('[HUEL] Error fetching SVG icon', error)
      svg = getFallbackIcon()
      sessionStorage.setItem(name, svg)
    }
  }

  return svg
}

export interface IconProps {
  name: IconNames
  width?: string
  height?: string
  color?: IconColours
  ariaHidden?: boolean
  customClasses?: string
}

export const Icon = ({ name, width, height, color = 'black', ariaHidden = true, customClasses }: IconProps) => {
  const [svgContent, setSvgContent] = useState<string | null>(null)

  useEffect(() => {
    const loadSvg = async () => {
      const content = await getSvg(name)
      setSvgContent(content)
    }

    loadSvg().catch(console.error)
  }, [name])

  return (
    <div
      style={
        {
          '--icon-width': width,
          '--icon-height': height,
        } as CSSProperties
      }
      className={classNames(styles.Icon, styles[`Icon--${color}`], customClasses)}
      dangerouslySetInnerHTML={{ __html: svgContent || '' }}
      aria-hidden={ariaHidden}
    />
  )
}
